(ns ductile.ui.code
  (:require ["@codemirror/autocomplete" :refer [autocompletion]]
            ["@codemirror/language" :refer [syntaxTree]]
            ["@codemirror/state" :refer [EditorState]]
            ["@codemirror/view" :as cm-view :refer [keymap]]
            [applied-science.js-interop :as j]
            [clojure.string :as str]
            [clojure.walk :as walk]
            [ductile.ui.deframe :as deframe]
            [nextjournal.clerk.render.code :as code]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clojure-mode.extensions.eval-region :as eval-region]
            [nextjournal.clojure-mode.keymap :as clojure-mode.keymap]
            [sci.core :as sci]
            [sci.ctx-store]
            [sci.nrepl.completions :as sci-completions]))

(defn eval-string
  ([source] (eval-string (sci.ctx-store/get-ctx) source))
  ([ctx source]
   (when-some [code (not-empty (str/trim source))]
     (let [{:keys [val ns]} (sci/eval-string+ ctx code)]
       (sci/alter-var-root sci/ns (constantly ns))
       val))))

(j/defn eval-at-cursor [on-result ^:js {:keys [state]}]
  (some->> (eval-region/cursor-node-string state)
           (eval-string)
           (on-result))
  true)

(j/defn eval-top-level [on-result ^:js {:keys [state]}]
  (some->> (eval-region/top-level-string state)
           (eval-string)
           (on-result))
  true)

(j/defn eval-cell [on-result ^:js {:keys [state]}]
  (-> (.-doc state)
      (str)
      (eval-string)
      (on-result))
  true)

(defn dev-autocomplete [^js context]
  (let [node-before (.. (syntaxTree (.-state context)) (resolveInner (.-pos context) -1))
        text-before (.. context -state (sliceDoc (.-from node-before) (.-pos context)))]
    #js {:from (.-from node-before)
         :options (clj->js (->> (:completions (sci-completions/completions {:ctx (sci.ctx-store/get-ctx) :ns "user" :symbol text-before}))
                                (map walk/keywordize-keys)
                                (map (fn [{:as option :keys [candidate info]}]
                                       (let [{:keys [arglists arglists-str]} info]
                                         (cond-> {:label candidate :type (if arglists "function" "namespace")}
                                           arglists (assoc :detail arglists-str)))))))}))

(def completion-source
  (autocompletion #js {:override #js [dev-autocomplete]}))

(defn info-at-point [^js view pos]
  (let [node-before (.. (syntaxTree (.-state view)) (resolveInner pos -1))
        text-at-point (.. view -state (sliceDoc (.-from node-before) (.-to node-before)))]
    (try
      (some->> (sci-completions/completions {:ctx (sci.ctx-store/get-ctx) :ns "user" :symbol text-at-point})
               :completions
               (map walk/keywordize-keys)
               (filter #(= (:candidate %) text-at-point))
               first
               :info)
      (catch js/Error e (js/console.log "caught")))))

(defn suggestions-at-point-ext [!view]
  (.. EditorState -transactionExtender
      (of (fn [^js tr]
            (when (.-selection tr)
              (when-let [to (.-to (first (.. tr -selection asSingle -ranges)))]
                (deframe/tx! [[:op/merge-at ::state {:show-docstring? false
                                                     :info (info-at-point @!view to)}]])))
            #js {}))))

(defn editor [{:keys [value on-change class suggestions? placeholder allow-tab-out? style] :or {value ""}}]
  (let [!container-el (hooks/use-ref nil)
        !editor-el (hooks/use-ref nil)
        !view (hooks/use-ref nil)
        !height (hooks/use-state nil)
        {:keys [show-docstring? info]} @(deframe/!get ::state)]
    (hooks/use-effect
     (fn []
       (let [resize-observer (js/ResizeObserver. #(reset! !height (or (j/get-in % [0 :borderBoxSize 0 :blockSize])
                                                                      (j/get-in % [0 :blockSize]))))
             view (code/make-view
                   (code/make-state
                    value
                    (cond-> code/default-extensions
                      true (.concat #js [(cm-view/placeholder (or placeholder "Type code here…"))
                                         (.of keymap clojure-mode.keymap/paredit)
                                         (.. EditorState -transactionExtender
                                             (of (fn [^js tr]
                                                   (when (.-docChanged tr)
                                                     (on-change (.. tr -state sliceDoc)))
                                                   #js {})))
                                         (.of keymap
                                              (j/lit
                                               [{:key "Mod-i"
                                                 :preventDefault true
                                                 :run #(deframe/tx! [[:op/assoc-in [::state :show-docstring?] (not show-docstring?)]])}
                                                {:key "Escape"
                                                 :run #(deframe/tx! [[:op/assoc-in [::state :show-docstring?] false]])}]))])
                      suggestions? (.concat #js [completion-source
                                                 (suggestions-at-point-ext !view)])))
                   @!editor-el)
             tab-handler (when allow-tab-out?
                           (fn [^js e]
                             (when (= (.-key e) "Tab")
                               (prn ::allow-tab-out)
                               (.stopPropagation e))))]
         (.observe resize-observer @!container-el)
         (reset! !view view)
         (.focus view)
         (when tab-handler (.addEventListener (.-dom view) "keydown" tab-handler true))
         (fn []
           (when tab-handler (.removeEventListener (.-dom view) "keydown" tab-handler true))
           (.disconnect resize-observer)
           (.destroy view)))))
    (hooks/use-effect
     (fn []
       (let [^js state (.-state @!view)]
         (when (not= value (.sliceDoc state))
           (.dispatch @!view
                      (.update state
                               (j/lit {:changes [{:insert value
                                                  :from 0 :to (.. state -doc -length)}]}))))))
     [value])
    [:div {:ref !container-el
           :class [class
                   (when (= style :input)
                     "bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded has-[.cm-focused]:ring-2 ring-indigo-500 ring-offset-2 shadow-[inset_0_2px_4px_rgba(0,0,0,0.04)] cursor-text px-2 py-0.5 text-xs leading-tight")]}
     [:div.h-full.w-full {:ref !editor-el}]]))
